/**
 * Tenant sharing: permissions are encoded in existing array columns as strings:
 *   "recipient-uuid:role"   (canonical — also accepts "recipient-uuid role")
 *   "recipient-uuid"          (legacy — treated as admin)
 *
 * notes-c.shared_users: TEXT[] (migrate from UUID[] — see SQL).
 * users.shared_users (TEXT/JSON): JSON array of the same string encodings for "share all".
 *
 * notes-c.user_id remains the row owner only (unchanged).
 */

export const SHARE_ROLES = {
  VIEW: "view",
  WRITE: "write",
  ADMIN: "admin",
};

/** @param {string} r */
export function normalizeShareRole(r) {
  const s = String(r || "")
    .trim()
    .toLowerCase();
  if (s === "viewer" || s === "read" || s === "view") return SHARE_ROLES.VIEW;
  if (s === "editor" || s === "write") return SHARE_ROLES.WRITE;
  if (s === "admin") return SHARE_ROLES.ADMIN;
  return SHARE_ROLES.ADMIN;
}

const UUID_RE =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?::|\s+)(view|write|admin)$/i;

/**
 * Encode one shared user for storage in shared_users TEXT[] or users JSON array.
 * Canonical form: "uuid:role"
 * @param {string} userId
 * @param {string} role
 */
export function encodeSharedUserEntry(userId, role) {
  return `${userId}:${normalizeShareRole(role)}`;
}

/**
 * Parse a single shared_users[] entry.
 * @param {unknown} entry
 * @returns {{ id: string, role: string } | null}
 */
export function parseSharedUserEntry(entry) {
  if (entry == null) return null;
  const s = String(entry).trim();
  const m = UUID_RE.exec(s);
  if (m) {
    return { id: m[1].toLowerCase(), role: normalizeShareRole(m[2]) };
  }
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  ) {
    return { id: s.toLowerCase(), role: SHARE_ROLES.ADMIN };
  }
  return null;
}

const ROLE_RANK = {
  [SHARE_ROLES.VIEW]: 0,
  [SHARE_ROLES.WRITE]: 1,
  [SHARE_ROLES.ADMIN]: 2,
};

/**
 * When the same user appears on multiple rows (or bare uuid vs uuid:role), prefer the
 * most restrictive role for UI (view < write < admin).
 */
export function pickMoreRestrictiveShareRole(a, b) {
  const ra = normalizeShareRole(a);
  const rb = normalizeShareRole(b);
  return ROLE_RANK[ra] <= ROLE_RANK[rb] ? ra : rb;
}

/** Prefer higher privilege (view < write < admin) when the same id appears more than once. */
export function pickLeastRestrictiveShareRole(a, b) {
  const ra = normalizeShareRole(a);
  const rb = normalizeShareRole(b);
  return ROLE_RANK[ra] >= ROLE_RANK[rb] ? ra : rb;
}

/**
 * Merge incoming id→role into target (mutates target). Keys normalized to lowercase.
 */
export function mergeShareRoleMaps(target, incoming) {
  if (!target || !incoming || typeof incoming !== "object") return target;
  for (const [id, role] of Object.entries(incoming)) {
    const kid = String(id).toLowerCase();
    const r = normalizeShareRole(role);
    if (target[kid] == null) target[kid] = r;
    else target[kid] = pickMoreRestrictiveShareRole(target[kid], r);
  }
  return target;
}

/** Build id → role map from encoded shared_users TEXT[] entries. */
export function roleMapFromSharedUsers(sharedUsers) {
  const m = {};
  for (const e of Array.isArray(sharedUsers) ? sharedUsers : []) {
    const p = parseSharedUserEntry(e);
    if (p) {
      if (m[p.id] == null) m[p.id] = p.role;
      else m[p.id] = pickMoreRestrictiveShareRole(m[p.id], p.role);
    }
  }
  return m;
}

/**
 * Resolve role for a user id from a map that may use mixed-case keys (e.g. from API).
 */
export function getSharedUserRoleFromMap(sharedUserRoles, userId) {
  if (sharedUserRoles == null || userId == null) return null;
  const k = String(userId).toLowerCase();
  if (Object.prototype.hasOwnProperty.call(sharedUserRoles, k)) {
    return normalizeShareRole(sharedUserRoles[k]);
  }
  const found = Object.entries(sharedUserRoles).find(
    ([key]) => String(key).toLowerCase() === k,
  );
  return found ? normalizeShareRole(found[1]) : null;
}

/** @param {unknown[]} sharedUsers */
export function getRoleForSharedUser(sharedUsers, viewerId) {
  const vid = String(viewerId || "").toLowerCase();
  const arr = Array.isArray(sharedUsers) ? sharedUsers : [];
  let best = null;
  for (const e of arr) {
    const p = parseSharedUserEntry(e);
    if (p && p.id === vid) {
      best =
        best == null
          ? p.role
          : pickLeastRestrictiveShareRole(best, p.role);
    }
  }
  return best;
}

/** @param {unknown[]} sharedUsers @param {string} userId */
export function sharedUsersHasViewer(sharedUsers, userId) {
  return getRoleForSharedUser(sharedUsers, userId) != null;
}

/**
 * Add or replace encoded entry for userId; drops legacy bare uuid for same user if present.
 * @param {unknown[]} sharedUsers
 * @param {string} userId
 * @param {string} role
 */
export function mergeSharedUsersAdd(sharedUsers, userId, role) {
  const arr = Array.isArray(sharedUsers) ? [...sharedUsers] : [];
  const uid = String(userId).toLowerCase();
  const filtered = arr.filter((e) => {
    const p = parseSharedUserEntry(e);
    if (!p) return true;
    return p.id !== uid;
  });
  filtered.push(encodeSharedUserEntry(uid, role));
  return dedupeSharedUsersArray(filtered);
}

/**
 * Collapse duplicate entries for the same UUID (e.g. legacy bare uuid + uuid:write)
 * into a single encoded string using the least restrictive role.
 */
export function dedupeSharedUsersArray(sharedUsers) {
  const arr = Array.isArray(sharedUsers) ? sharedUsers : [];
  const byId = new Map();
  const unparsed = [];
  for (const e of arr) {
    const p = parseSharedUserEntry(e);
    if (!p) {
      unparsed.push(e);
      continue;
    }
    const prev = byId.get(p.id);
    byId.set(
      p.id,
      prev == null ? p.role : pickLeastRestrictiveShareRole(prev, p.role),
    );
  }
  const merged = [...byId.entries()].map(([id, role]) =>
    encodeSharedUserEntry(id, role),
  );
  return [...merged, ...unparsed];
}

/** @param {unknown[]} sharedUsers @param {string} userId */
export function mergeSharedUsersRemove(sharedUsers, userId) {
  const arr = Array.isArray(sharedUsers) ? sharedUsers : [];
  const uid = String(userId).toLowerCase();
  return arr.filter((e) => {
    const p = parseSharedUserEntry(e);
    if (!p) return true;
    return p.id !== uid;
  });
}

/**
 * PostgREST .or() clauses for "current user appears in notes-c.shared_users".
 *
 * With `text[]` and encoded entries (`uuid:view`, `uuid:write`, `uuid:admin`), the
 * row must match overlap on those strings — bare `shared_users.ov.{uuid}` alone
 * does NOT match an element like `uuid:write`.
 *
 * Default: all variants (legacy bare uuid + :view/:write/:admin). Set
 * `REACT_APP_SHARE_ENCODED_OVERLAP=false` only if `shared_users` is still `uuid[]`
 * and only plain UUIDs are stored (otherwise PostgREST returns 400 on :role terms).
 *
 * @param {string} userId
 */
export function sharedUsersOverlapOrClauses(userId) {
  const id = String(userId);
  const legacy = [`shared_users.ov.{${id}}`];
  const legacyUuidArrayOnly =
    typeof process !== "undefined" &&
    process.env.REACT_APP_SHARE_ENCODED_OVERLAP === "false";
  if (legacyUuidArrayOnly) return legacy;
  return [
    ...legacy,
    `shared_users.ov.{${id}:${SHARE_ROLES.VIEW}}`,
    `shared_users.ov.{${id}:${SHARE_ROLES.WRITE}}`,
    `shared_users.ov.{${id}:${SHARE_ROLES.ADMIN}}`,
  ];
}

/**
 * Parse owner's users.shared_users (share-all). Supports:
 * - "uuid:role" / "uuid role"
 * - plain "uuid" (admin)
 * - legacy { id, role } objects
 * - recipient row-id strings (no uuid:role match): skipped for share-all matching in backend
 * @param {unknown} raw
 * @returns {{ entries: Array<{ id: string, role: string }> }}
 */
export function parseOwnerShareAllEntries(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { entries: [] };
    }
  }
  if (!Array.isArray(parsed)) return { entries: [] };
  const entries = [];
  for (const item of parsed) {
    if (typeof item === "string") {
      const p = parseSharedUserEntry(item);
      if (p) entries.push({ id: p.id, role: p.role });
    } else if (item && typeof item === "object" && item.id) {
      entries.push({
        id: String(item.id),
        role: normalizeShareRole(item.role),
      });
    }
  }
  return { entries };
}

/** @param {Array<{ id: string, role: string }>} entries */
export function serializeOwnerShareAllEntries(entries) {
  return entries.map((e) => encodeSharedUserEntry(e.id, e.role));
}

/**
 * @param {string} role
 */
export function capabilitiesFromShareRole(role) {
  const r = normalizeShareRole(role);
  if (r === SHARE_ROLES.VIEW) {
    return {
      level: "viewer",
      canEditDocument: false,
      canEditScheduleFields: false,
      canDeleteRow: false,
      canShareRow: false,
    };
  }
  if (r === SHARE_ROLES.WRITE) {
    return {
      level: "editor",
      canEditDocument: true,
      canEditScheduleFields: false,
      canDeleteRow: false,
      canShareRow: false,
    };
  }
  return {
    level: "admin",
    canEditDocument: true,
    canEditScheduleFields: true,
    canDeleteRow: true,
    canShareRow: true,
  };
}

const NONE = {
  level: "none",
  canEditDocument: false,
  canEditScheduleFields: false,
  canDeleteRow: false,
  canShareRow: false,
};

/**
 * Human label for a {@link resolveTenantRowAccess} level. Returns null for
 * levels that should not be surfaced as a badge (e.g. `none`).
 * @param {string|null|undefined} level
 */
export function tenantAccessLevelLabel(level) {
  switch (level) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "editor":
      return "Editor";
    case "viewer":
      return "Read";
    default:
      return null;
  }
}

/** Chakra colorScheme for the badge that displays {@link tenantAccessLevelLabel}. */
export function tenantAccessLevelColorScheme(level) {
  switch (level) {
    case "owner":
      return "purple";
    case "admin":
      return "blue";
    case "editor":
      return "green";
    case "viewer":
      return "gray";
    default:
      return "gray";
  }
}

/**
 * Tenant collaborators: may edit document body, save to storage, and use version history
 * (manual save version, restore, auto-version triggers tied to editing).
 * Viewer (view) is excluded. Uses {@link capabilitiesFromShareRole} levels when
 * {@link resolveTenantRowAccess} has run; falls back to canEditDocument.
 *
 * @param {{ canEditDocument?: boolean, level?: string } | null | undefined} access
 */
export function tenantShareAllowsDocumentEdit(access) {
  if (!access || typeof access !== "object") return false;
  if (access.canEditDocument === true) return true;
  const level = access.level;
  return (
    level === "editor" || level === "admin" || level === "owner"
  );
}

/**
 * @param {object} p
 * @param {string|null} p.viewerId
 * @param {string|null} p.rowUserId
 * @param {string[]|null} p.sharedUsers notes-c.shared_users (TEXT[] of encoded strings)
 * @param {string[]|null} p.shareAllOwnerIds
 * @param {Record<string,string>|null} p.shareAllOwnerRoles
 */
export function resolveTenantRowAccess({
  viewerId,
  rowUserId,
  sharedUsers,
  shareAllOwnerIds,
  shareAllOwnerRoles,
}) {
  if (!viewerId || !rowUserId) return NONE;
  if (viewerId === rowUserId) {
    return {
      level: "owner",
      canEditDocument: true,
      canEditScheduleFields: true,
      canDeleteRow: true,
      canShareRow: true,
    };
  }

  const su = Array.isArray(sharedUsers) ? sharedUsers : [];
  const roleDirect = getRoleForSharedUser(su, viewerId);
  const owners = Array.isArray(shareAllOwnerIds) ? shareAllOwnerIds : [];
  const rid = String(rowUserId ?? "").trim();
  const viaShareAll = owners.some((o) => String(o).trim() === rid);

  if (roleDirect != null) {
    return capabilitiesFromShareRole(roleDirect);
  }

  if (viaShareAll) {
    const rolesObj =
      shareAllOwnerRoles && typeof shareAllOwnerRoles === "object"
        ? shareAllOwnerRoles
        : null;
    const or =
      rolesObj != null
        ? rolesObj[rowUserId] ?? rolesObj[rid] ?? rolesObj[String(rowUserId)]
        : null;
    const role = or != null ? normalizeShareRole(or) : SHARE_ROLES.ADMIN;
    return capabilitiesFromShareRole(role);
  }

  return NONE;
}
